import type { IntentSection, IntentFAQ, RelatedLink } from "@/components/seo/IntentGuide";

export interface IntentGuideData {
  slug: string;
  path: string;
  eyebrow: string;
  h1: string;
  title: string;
  description: string;
  intro: string;
  sections: IntentSection[];
  faqs: IntentFAQ[];
  related: RelatedLink[];
  priority: string;
}

const baseRelated: RelatedLink[] = [
  { label: "Best Pool Villas Hua Hin 2026", to: "/best-premium-pool-villas-hua-hin-2026" },
  { label: "Hua Hin Villa Market 2026 Guide", to: "/hua-hin-villa-market-2026-developer-guide" },
  { label: "New Developments Hua Hin", to: "/new-villa-developments-hua-hin" },
];

export const intentGuides: IntentGuideData[] = [
  {
    slug: "buy-property-hua-hin-thailand",
    path: "/buy-property-hua-hin-thailand",
    priority: "0.9",
    eyebrow: "Hua Hin Buyer's Guide",
    h1: "How to Buy Property in Hua Hin, Thailand (2026 Guide)",
    title: "Buy Property in Hua Hin 2026 | Step-by-Step Buyer's Guide",
    description:
      "Independent guide to buying real estate in Hua Hin, Thailand: legal process, costs, taxes, freehold vs leasehold, and the best areas for foreign buyers in 2026.",
    intro:
      "Buying property in Hua Hin involves Thai legal specifics that differ from most Western markets. This guide walks through the entire purchase journey — from due diligence to title transfer — for foreign and Thai buyers in 2026.",
    sections: [
      {
        heading: "Step-by-Step Purchase Process",
        body: "A typical Hua Hin property purchase moves through six clear stages. Each step has standard timelines and documentation requirements that buyers should understand before signing anything.",
        bullets: [
          "Property search and shortlisting (2–8 weeks)",
          "Reservation agreement and refundable deposit (typically 100,000–200,000 THB)",
          "Legal due diligence on title, land office records, and developer history",
          "Sales and Purchase Agreement (SPA) signing",
          "Foreign Exchange Transaction Form (FET) for transfers above 50,000 USD",
          "Title transfer at the Land Office and payment of taxes/fees",
        ],
      },
      {
        heading: "Costs Beyond the Purchase Price",
        body: "Total acquisition cost in Thailand typically adds 4–6% on top of the headline price. The split between buyer and seller is negotiable but often shared 50/50 in Hua Hin.",
        bullets: [
          "Transfer fee: 2% of appraised value",
          "Specific Business Tax: 3.3% (if seller held under 5 years)",
          "Stamp duty: 0.5% (if SBT not applicable)",
          "Withholding tax: progressive, calculated by the Land Office",
          "Legal fees: 40,000–80,000 THB for proper conveyancing",
        ],
      },
      {
        heading: "Best Areas to Buy in Hua Hin",
        body: [
          "Hua Hin's micro-markets each suit different lifestyles and budgets. Beachside Hua Hin Town offers walkability and rental yield. Khao Tao and Khao Takiab attract retirees. Black Mountain and Hin Lek Fai dominate the golf villa segment, while Khao Noi / Soi Chonprathan 44 has emerged as a value sweet spot for new pool villa developments.",
          "Cha-Am to the north and Pranburi to the south extend the market for buyers seeking lower price points or larger plots.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can foreigners buy property in Hua Hin?",
        a: "Foreigners cannot directly own land in Thailand but can own the building (villa) freehold and hold land via a registered 30-year lease (renewable), a Thai limited company structure, or — for condominiums — direct freehold ownership up to the 49% foreign quota.",
      },
      {
        q: "How long does a property purchase take in Hua Hin?",
        a: "From signed reservation to title transfer, an off-plan villa purchase usually takes 30–90 days for completed properties and follows the construction schedule for new builds. Due diligence alone takes 7–14 days.",
      },
      {
        q: "Are property prices in Hua Hin negotiable?",
        a: "Yes. Resale and developer-direct properties typically have 3–10% negotiation room. New launches and limited-availability projects offer less flexibility but may include incentives such as furniture packages or paid transfer fees.",
      },
      {
        q: "Do I need a lawyer to buy property in Hua Hin?",
        a: "Strongly recommended. An independent Thai property lawyer protects the buyer through title search, contract review, and Land Office representation — distinct from the developer's or agent's lawyer who represents the seller.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "rent-villa-hua-hin",
    path: "/rent-villa-hua-hin",
    priority: "0.8",
    eyebrow: "Hua Hin Rental Market",
    h1: "Renting a Villa in Hua Hin: Long-Term & Holiday Rentals 2026",
    title: "Rent a Villa in Hua Hin 2026 | Long-Term & Holiday Rentals",
    description:
      "Hua Hin villa rental guide: monthly rates, contract terms, deposit norms, best areas, and how rental yields compare for property investors in 2026.",
    intro:
      "Hua Hin's rental market splits cleanly into long-term residential rentals (6–12 month leases) and short-term holiday rentals. Knowing the rates, norms, and legal limits helps both tenants and investor-owners.",
    sections: [
      {
        heading: "Typical Monthly Rental Rates (2026)",
        body: "Rates vary by area, age of the villa, pool, and proximity to the beach. The figures below reflect long-term unfurnished to fully-furnished pool villas.",
        bullets: [
          "2-bed pool villa, inland: 25,000–40,000 THB/month",
          "3-bed pool villa, suburban: 45,000–80,000 THB/month",
          "3-bed beachside or golf-front: 80,000–150,000 THB/month",
          "4-bed luxury villa, prime location: 150,000–300,000+ THB/month",
        ],
      },
      {
        heading: "Lease Terms & Deposit Norms",
        body: "Standard long-term leases run 12 months with 2 months' deposit and 1 month paid in advance. Utilities are billed separately. Short stays (under 30 days) fall under hotel licensing rules — most condos and many villa estates restrict daily rentals.",
      },
      {
        heading: "Rental Yield for Investors",
        body: "Gross yields in Hua Hin sit between 4–7% for well-located pool villas, with the higher end achieved through professionally managed mixed long/short-term strategies. Net yields after management, maintenance, and tax sit around 3–5%.",
      },
    ],
    faqs: [
      {
        q: "Can I rent out my Hua Hin villa on Airbnb?",
        a: "Daily rentals under 30 days are technically restricted to licensed hotels under Thai law. Many estates and condos enforce this; villa owners typically focus on monthly+ rentals, which are fully legal and have lower management overhead.",
      },
      {
        q: "What's the rental high season in Hua Hin?",
        a: "November to March is peak season — cool, dry weather attracts long-stay European and Russian visitors. April to October sees lower occupancy but consistent demand from regional and domestic travelers.",
      },
      {
        q: "Are utilities included in long-term Hua Hin rentals?",
        a: "Usually not. Tenants pay electricity, water, and internet directly. Pool maintenance and gardening are sometimes included for furnished villas — always clarified in the lease.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "invest-real-estate-hua-hin",
    path: "/invest-real-estate-hua-hin",
    priority: "0.9",
    eyebrow: "Hua Hin Investment Outlook",
    h1: "Investing in Hua Hin Real Estate: 2026 Market Outlook",
    title: "Hua Hin Real Estate Investment 2026 | Yields, Trends, Outlook",
    description:
      "Independent 2026 investment analysis for Hua Hin property: price growth, rental yields, infrastructure pipeline, exit liquidity, and risk factors.",
    intro:
      "Hua Hin has matured from a weekend Bangkok escape into a tier-2 international resort market. This analysis lays out the data and structural factors investors should weigh before committing capital in 2026.",
    sections: [
      {
        heading: "Price Growth & Yield Snapshot",
        body: "Prime Hua Hin villas have appreciated 3–7% annually over the last cycle, with the 10–15M THB pool villa segment showing the most consistent demand. Gross rental yields of 4–7% remain attractive relative to Bangkok condos (3–5%).",
      },
      {
        heading: "Infrastructure Catalysts (2026–2030)",
        body: "Several public investments are reshaping Hua Hin's accessibility and tier:",
        bullets: [
          "Bangkok–Hua Hin dual-track railway (under construction)",
          "Motorway 8 extension reducing Bangkok drive time toward 2 hours",
          "Hua Hin Airport upgrades targeting more regional routes",
          "Continuous expansion of international hospitals and schools",
        ],
      },
      {
        heading: "Risks Investors Should Price In",
        body: "Currency exposure, evolving foreign-ownership regulations, oversupply in specific segments (small condos), and developer execution risk on off-plan purchases are the most material risks. Working with established legal counsel and developers with completed projects mitigates most of these.",
      },
    ],
    faqs: [
      {
        q: "Is Hua Hin a better property investment than Phuket?",
        a: "Hua Hin offers lower entry prices, simpler logistics from Bangkok, and a more stable resident expat base. Phuket has higher tourist volumes and stronger short-term rental yields but greater seasonality and higher prices per square meter.",
      },
      {
        q: "What's the minimum realistic investment in Hua Hin?",
        a: "Entry-level studio condos start around 1.5–2M THB. For pool villas with meaningful rental potential, the practical floor is 7–10M THB.",
      },
      {
        q: "How liquid is the Hua Hin resale market?",
        a: "Mid-market villas (10–20M THB) in established areas typically sell within 6–12 months at fair pricing. Ultra-luxury and unusual properties can take longer. Eco-friendly and energy-efficient features increasingly support resale value.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "build-villa-hua-hin",
    path: "/build-villa-hua-hin",
    priority: "0.8",
    eyebrow: "Hua Hin Construction Guide",
    h1: "Building a Villa in Hua Hin: Costs, Permits & Contractors",
    title: "Build a Villa in Hua Hin 2026 | Construction Costs & Process",
    description:
      "Custom villa construction in Hua Hin: build costs per sqm, land purchase, permits, contractor selection, timelines and how it compares to buying off-plan.",
    intro:
      "Building a villa from scratch on a self-purchased plot offers customization and potential cost savings — but adds complexity around permits, supervision, and construction risk. Here is what to expect in Hua Hin in 2026.",
    sections: [
      {
        heading: "Construction Cost per Square Meter",
        body: "Costs depend on finish quality and design complexity. Use these 2026 ranges as a planning baseline.",
        bullets: [
          "Standard build quality: 25,000–35,000 THB/sqm",
          "Mid-tier with branded fixtures: 35,000–50,000 THB/sqm",
          "High-end with eco features (solar, double glazing, saltwater pool): 50,000–80,000+ THB/sqm",
          "Pool: 600,000–1,500,000 THB additional",
          "Landscaping and walls: 300,000–800,000 THB additional",
        ],
      },
      {
        heading: "Permits and Approvals",
        body: "Building permits are issued by the local Tessaban (municipal office). Plans must be sealed by a licensed Thai architect and engineer. Allow 30–90 days for approval depending on plot zoning and project size.",
      },
      {
        heading: "Build vs Buy Comparison",
        body: "Custom builds usually take 10–14 months and require active supervision. Buying a finished or off-plan villa from an established developer trades flexibility for predictability and warranty coverage. Many buyers choosing the developer route cite construction risk and supervision distance as the deciding factor.",
      },
    ],
    faqs: [
      {
        q: "Can a foreigner build a villa in Hua Hin?",
        a: "Yes. The villa structure can be owned freehold by a foreigner. The land underneath must be held via a long-term lease, Thai company, or Thai spouse — exactly the same structure as buying an existing villa.",
      },
      {
        q: "How long does it take to build a villa in Hua Hin?",
        a: "Plan on 10–14 months for a standard 3-bed pool villa from groundbreaking to handover, plus 2–4 months upfront for design and permits.",
      },
      {
        q: "Is it cheaper to build than to buy in Hua Hin?",
        a: "Sometimes 10–20% cheaper per square meter — but only if land is bought well, the contractor is competent, and the owner has bandwidth for active supervision. For overseas buyers, the implicit time and risk cost often closes the gap.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "renovate-villa-hua-hin",
    path: "/renovate-villa-hua-hin",
    priority: "0.7",
    eyebrow: "Hua Hin Renovation Guide",
    h1: "Renovating a Villa in Hua Hin: Costs, Contractors & ROI",
    title: "Villa Renovation Hua Hin 2026 | Costs, Contractors, ROI",
    description:
      "Practical 2026 guide to renovating a villa in Hua Hin: typical costs by scope, finding reliable contractors, permits, and which upgrades return the most at resale.",
    intro:
      "Older Hua Hin villas — many built in the 2000s boom — increasingly come to market at attractive prices. Smart renovation can unlock significant value, especially when modernizing layouts and adding energy-efficient systems.",
    sections: [
      {
        heading: "Renovation Cost Ranges (2026)",
        body: "These ranges assume a 200–300 sqm villa and include materials, labor, and contractor margin.",
        bullets: [
          "Cosmetic refresh (paint, fixtures, soft furnishing): 200,000–600,000 THB",
          "Bathroom remodel: 80,000–250,000 THB per bathroom",
          "Kitchen replacement: 250,000–900,000 THB",
          "Pool resurfacing or salt-system conversion: 150,000–400,000 THB",
          "Solar panel system (5–10 kW with battery): 350,000–800,000 THB",
          "Full structural modernization: 8,000–15,000 THB/sqm",
        ],
      },
      {
        heading: "Highest-ROI Upgrades",
        body: "At resale, buyers in 2026 reward energy efficiency, modern open-plan kitchens, and updated bathrooms more than cosmetic-only refreshes. Solar systems and double glazing are increasingly expected at the premium end of the market.",
      },
      {
        heading: "Finding a Reliable Contractor",
        body: "Always view at least three completed projects, demand a stage-payment schedule tied to milestones (never large upfront payments), and use a written Thai/English contract. Local property lawyers can recommend vetted contractors.",
      },
    ],
    faqs: [
      {
        q: "Do I need a permit to renovate a villa in Hua Hin?",
        a: "Cosmetic and interior work generally does not require a permit. Structural changes, footprint extensions, and pool installations require approval from the local municipal office.",
      },
      {
        q: "How long does a villa renovation take in Hua Hin?",
        a: "Cosmetic renovations: 4–8 weeks. Bathroom or kitchen remodels: 6–10 weeks each. Full modernization: 4–7 months. Always add buffer for material lead times during peak season.",
      },
      {
        q: "Should I renovate or buy a new-build villa?",
        a: "If the existing structure is sound and the location is prime, renovation often delivers better total value. For buyers who want warranty coverage, energy-efficient features as standard, and zero project risk, a new development is the simpler path.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "hua-hin-property-developers",
    path: "/hua-hin-property-developers",
    priority: "0.8",
    eyebrow: "Hua Hin Developer Directory",
    h1: "Active Hua Hin Property Developers in 2026",
    title: "Hua Hin Property Developers 2026 | Active Builder Directory",
    description:
      "Directory of active villa and condo developers in Hua Hin for 2026, including project counts, segments served, and how to evaluate developer reliability.",
    intro:
      "Hua Hin's developer landscape ranges from 20-year veterans with dozens of completed projects to newer entrants with single signature developments. Choosing the right developer is often a bigger decision than choosing the project.",
    sections: [
      {
        heading: "How to Evaluate a Developer",
        body: "Headline marketing rarely tells the full story. Buyers should verify completion track record before committing capital.",
        bullets: [
          "Number of completed and handed-over projects",
          "Years operating under the same registered company",
          "Quality of after-sales service and warranty handling",
          "Financial structure of the project (escrow, staged payments)",
          "Independent reviews from current owners in past projects",
        ],
      },
      {
        heading: "Notable Active Developers",
        body: "The Hua Hin market includes Hillside Hamlet Homes, Botanica, Sivana Development, The Clouds, Orchid Palm Homes, Red Mountain, Smart House, TCH, Highland Villas, CASA Collina, and a growing wave of eco-focused entrants such as Siam Horizon. Each developer's Alternative Comparison page on this site covers segment, pricing, and typical specifications.",
      },
      {
        heading: "New vs Established Developers",
        body: "Established developers offer track record and resale predictability. Newer developers often deliver better specifications per THB — solar panels, saltwater pools, and double glazing as standard rather than upgrades — to differentiate. The right choice depends on the buyer's risk tolerance and priorities.",
      },
    ],
    faqs: [
      {
        q: "Who are the biggest property developers in Hua Hin?",
        a: "By project volume, established names include Hillside Hamlet Homes, Orchid Palm Homes, Botanica, and Sivana. By recent activity, multiple newer eco-focused developers have launched 2024–2026 projects.",
      },
      {
        q: "Are off-plan purchases safe in Hua Hin?",
        a: "Off-plan purchases are common and generally safe with reputable developers, staged payments tied to construction milestones, and proper legal review. Avoid any developer requiring large upfront cash payments outside formal escrow.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "hua-hin-areas-neighborhoods-guide",
    path: "/hua-hin-areas-neighborhoods-guide",
    priority: "0.8",
    eyebrow: "Hua Hin Area Guide",
    h1: "Hua Hin Areas & Neighborhoods: Where to Buy in 2026",
    title: "Hua Hin Neighborhoods Guide 2026 | Best Areas to Buy",
    description:
      "Comprehensive area-by-area guide to Hua Hin: Khao Tao, Khao Takiab, Black Mountain, Hin Lek Fai, Soi Chonprathan 44, Pranburi, Cha-Am — pricing, character, who buys where.",
    intro:
      "\"Hua Hin\" covers a 30 km coastal stretch of distinctly different neighborhoods. Where you buy shapes your daily lifestyle, rental potential, and resale liquidity more than any single villa specification.",
    sections: [
      {
        heading: "Hua Hin Town & Beachside",
        body: "Walkable, lively, with the highest density of restaurants and shops. Best for short-stay rental yield. Land is scarce — most new product is condos.",
      },
      {
        heading: "Khao Takiab & Khao Tao",
        body: "South of the main town, quieter beaches and a strong retiree presence. Khao Tao hosts some of the area's most expensive villas. Easy access to international hospitals.",
      },
      {
        heading: "Black Mountain & Hin Lek Fai",
        body: "Inland golf belt with mountain views. Larger plots, more privacy, lower density. Top choice for golf-oriented buyers and large-family villas.",
      },
      {
        heading: "Khao Noi / Soi Chonprathan 44",
        body: "An emerging value pocket close to the city, near Khao Noi Temple. New developments here offer modern specs at competitive prices and have become a focus of 2025–2026 launches, including projects like Siam Horizon.",
      },
      {
        heading: "Pranburi & Cha-Am",
        body: "Pranburi to the south offers genuinely quiet beaches and lower prices. Cha-Am to the north suits buyers prioritizing space and value, accepting longer drives to Hua Hin's amenities.",
      },
    ],
    faqs: [
      {
        q: "Which Hua Hin neighborhood has the best value?",
        a: "For new pool villas, the Khao Noi / Soi Chonprathan 44 area currently offers the strongest specifications-to-price ratio. For resale value, established estates around Black Mountain and Hin Lek Fai tend to hold price well.",
      },
      {
        q: "What's the safest area in Hua Hin for foreign residents?",
        a: "Hua Hin is broadly safe across all neighborhoods. Gated communities — most new pool villa estates qualify — add 24/7 security and tend to dominate the foreign-buyer segment.",
      },
    ],
    related: baseRelated,
  },
  {
    slug: "foreign-ownership-property-thailand-hua-hin",
    path: "/foreign-ownership-property-thailand-hua-hin",
    priority: "0.9",
    eyebrow: "Foreign Ownership in Thailand",
    h1: "Foreign Property Ownership in Hua Hin & Thailand: 2026 Legal Guide",
    title: "Foreign Property Ownership Thailand 2026 | Hua Hin Legal Guide",
    description:
      "Plain-English legal guide to foreign property ownership in Thailand and Hua Hin: leasehold, freehold villa structures, Thai company, condominium quota, and FET requirements.",
    intro:
      "Foreign property ownership in Thailand is fully legal but follows specific structures. Understanding the four main routes — and which suits your situation — is the foundation of any safe Hua Hin purchase.",
    sections: [
      {
        heading: "The Four Ownership Structures",
        body: "Foreign buyers in Thailand typically use one of these structures. The right choice depends on property type, budget, and long-term plans.",
        bullets: [
          "Condominium freehold — direct ownership within the building's 49% foreign quota",
          "Leasehold — registered 30-year lease on land, with renewal clauses",
          "Thai company — Thai-majority limited company holds the land, foreigner controls",
          "Thai spouse — land in spouse's name with appropriate prenuptial documentation",
        ],
      },
      {
        heading: "Foreign Exchange Transaction Form (FET)",
        body: "For condominium freehold purchases, funds must be transferred from abroad in foreign currency and converted to THB within Thailand. The bank issues a FET (formerly Tor Tor 3) for transfers over 50,000 USD — required at the Land Office.",
      },
      {
        heading: "Common Pitfalls to Avoid",
        body: "Nominee shareholder structures (Thai nominees holding shares only on paper for the foreigner) are explicitly illegal under Thai law. Verbal lease arrangements, unregistered renewal options, and failing to use a Thai-licensed lawyer all create avoidable risk.",
      },
    ],
    faqs: [
      {
        q: "Can foreigners buy property in Thailand?",
        a: "Yes, foreigners can buy condominiums within the 49% foreign quota and own villa structures freehold, though they cannot directly own land and must use leasehold, a Thai company, or a Thai spouse for the land underneath.",
      },
      {
        q: "Can Americans buy property in Thailand?",
        a: "Yes, Americans are treated the same as other foreigners under Thai property law and can acquire property through condominium freehold, leasehold, a Thai company, or a Thai spouse structure.",
      },
      {
        q: "Can US citizens buy property in Thailand?",
        a: "Yes, US citizens follow the same foreign-ownership rules as other non-Thai nationals and can legally buy property through the standard permitted structures.",
      },
      {
        q: "Can a foreigner own a villa in Hua Hin outright?",
        a: "A foreigner can own the villa structure freehold and hold the underlying land via a registered long-term lease, a properly structured Thai limited company, or a Thai spouse. Direct land ownership by foreign individuals is not permitted.",
      },
      {
        q: "Is leasehold safe for foreign buyers?",
        a: "A properly registered 30-year lease at the Land Office, with documented renewal mechanics and a separate freehold ownership of the building, is the most common and well-understood structure used by foreign buyers in Hua Hin.",
      },
      {
        q: "Can I get a Thai mortgage as a foreigner?",
        a: "Some Thai banks (UOB, MBK Guarantee, Bangkok Bank Singapore) offer mortgages to qualifying foreigners, typically requiring 30–50% down payment, proof of overseas income, and shorter terms than Thai-resident mortgages.",
      },
    ],
    related: baseRelated,
  },
];
import { projectPages } from "./projectPages";
intentGuides.push(...projectPages);
